// Carousel.tsx
import Map from '@/components/Map';
import classNames from 'classnames';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useSnapCarousel } from 'react-snap-carousel';
import ProgressBar from './ProgressBar';
import Div from '../Div';
import SlickArrow from '@/components/Icons/SlickArrow';
import Button from '../Button';

interface CarouselProps<T> {
  readonly list: T[];
  readonly render: (
    item: any,
    index: number,
    isSnapPoint: boolean,
  ) => React.ReactElement<CarouselItemProps>;
  readonly className?: string;
  hideProgressBar?: boolean;
}

const Carousel = <T extends any>({
  render,
  list,
  className,
  hideProgressBar = false,
}: CarouselProps<T>) => {
  const { scrollRef, pages, activePageIndex, prev, next, snapPointIndexes } = useSnapCarousel();

  const renderItem = useCallback(
    (item: any, index: number) => {
      return render(item, index, snapPointIndexes.has(index));
    },
    [render, snapPointIndexes],
  );

  const scrollPercentage = useMemo(() => {
    const activePage = activePageIndex + 1;
    const totalPages = pages.length;
    return String((activePage / totalPages) * 100) + '%';
  }, [activePageIndex, pages.length]);

  return (
    <Fragment>
      <Map
        wrapper={'ul'}
        className={classNames('relative flex overflow-auto snap-x hide-scrollbar', className)}
        ref={scrollRef}
        render={renderItem}
        list={list}
      />
      {pages.length === 1 || hideProgressBar ? null : (
        <Div className="flex md:gap-s6 items-center">
          <ProgressBar
            className="w-full"
            dataTestId="carousel-progressbar"
            progressBarPercentage={scrollPercentage}
          />
          <Button
            theme="icon"
            className={classNames('hidden md:inline-block rotate-180', {
              'opacity-50': activePageIndex <= 0,
            })}
            onClick={prev}
            disabled={activePageIndex <= 0}
            dataTestId="prev-btn"
          >
            <SlickArrow />
          </Button>
          <Button
            theme="icon"
            className={classNames('hidden md:inline-block', {
              'opacity-50': activePageIndex === pages.length - 1,
            })}
            onClick={next}
            disabled={activePageIndex === pages.length - 1}
            dataTestId="next-btn"
          >
            <SlickArrow />
          </Button>
        </Div>
      )}
    </Fragment>
  );
};

interface CarouselItemProps {
  readonly isSnapPoint: boolean;
  readonly children?: React.ReactNode;
  readonly className?: string;
}

export const CarouselItem = ({ isSnapPoint, children, className }: CarouselItemProps) => {
  return (
    <li
      className={classNames(
        {
          'snap-start': isSnapPoint,
        },
        className,
      )}
    >
      {children}
    </li>
  );
};

export default Carousel;
